/**
 * UserForm Component
 * Form for creating and editing user profiles
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type {
  AdminUser,
  CreateUserPayload,
  UpdateUserPayload,
  UserRole,
  AccountStatus,
} from '../types/admin-user.type';
import { PartnerCompanySelect } from '@/features/partner-companies/components/PartnerCompanySelect';
import type { PartnerCompanyOption } from '@/features/partner-companies/types/partner-company.types';

/** Feature 100 — create-mode prefill from a /partner-trial request. */
export type TrialRequestPrefill = {
  trialRequestId: string;
  email: string;
  name: string;
  companyName?: string;
  trialDays?: number;
};

type UserFormProps = {
  user?: AdminUser;
  /** When set (create mode), prefills an FP-as-trial creation from a trial request. */
  trialPrefill?: TrialRequestPrefill;
  onSubmit: (payload: CreateUserPayload | UpdateUserPayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  accountStatus: AccountStatus;
  isActive: boolean;
};

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'CARE_SEEKER', label: 'Care Seeker' },
  { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
  { value: 'FINANCIAL_ADVISOR', label: 'Financial Advisor' },
];

const STATUS_OPTIONS: Array<{ value: AccountStatus; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'PENDING', label: 'Pending' },
];

/**
 * User form component for create/edit operations
 * 
 * @param user - Existing user to edit (undefined for create mode)
 * @param onSubmit - Callback when form is submitted
 * @param onCancel - Callback when form is cancelled
 * @param isSubmitting - Whether form is currently submitting
 * 
 * Features:
 * - Validation for required fields
 * - Email format validation
 * - Create vs Edit mode
 * - Loading state during submission
 */
export function UserForm({
  user,
  trialPrefill,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: UserFormProps) {
  const isEditMode = !!user;

  // Feature 100: prefill from a trial request splits "First Last" like the backend does.
  const prefillSpace = trialPrefill?.name.indexOf(' ') ?? -1;
  const prefillFirst =
    prefillSpace > 0 ? trialPrefill!.name.slice(0, prefillSpace) : (trialPrefill?.name ?? '');
  const prefillLast = prefillSpace > 0 ? trialPrefill!.name.slice(prefillSpace + 1).trim() : '';

  const [formData, setFormData] = useState<FormData>({
    email: user?.email || trialPrefill?.email || '',
    firstName: user?.firstName || prefillFirst,
    lastName: user?.lastName || prefillLast,
    role: user?.role || (trialPrefill ? 'FINANCIAL_ADVISOR' : 'CARE_SEEKER'),
    accountStatus: user?.accountStatus || 'ACTIVE',
    isActive: user?.isActive ?? true,
  });

  const [partnerCompany, setPartnerCompany] = useState<PartnerCompanyOption | null>(
    user?.partnerCompany || null,
  );

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  // Feature 100 — create-as-trial controls (create mode, FP role only)
  const [asTrial, setAsTrial] = useState(!!trialPrefill);
  const [trialDays, setTrialDays] = useState(String(trialPrefill?.trialDays ?? 7));
  const [trialDaysError, setTrialDaysError] = useState<string | undefined>();
  const isProfessionalRole =
    formData.role === 'SERVICE_PROVIDER' || formData.role === 'FINANCIAL_ADVISOR';

  // Update form when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName || '',
        role: user.role,
        accountStatus: user.accountStatus,
        isActive: user.isActive,
      });
      setPartnerCompany(user.partnerCompany || null);
    }
  }, [user]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Feature 100 — a ticked trial box must produce a trial account. Blocking
    // here stops a blank/out-of-range length from silently creating a regular
    // FP instead (backend accepts 1–365).
    const parsedTrialDays = Number.parseInt(trialDays, 10);
    const trialDaysInvalid =
      !isEditMode &&
      asTrial &&
      formData.role === 'FINANCIAL_ADVISOR' &&
      !(parsedTrialDays >= 1 && parsedTrialDays <= 365);
    setTrialDaysError(trialDaysInvalid ? 'Enter a trial length between 1 and 365 days' : undefined);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !trialDaysInvalid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Combine firstName and lastName into single 'name' field for backend
      const fullName = [formData.firstName.trim(), formData.lastName.trim()]
        .filter(Boolean)
        .join(' ');

      if (isEditMode) {
        // Update mode - send only changed fields
        const payload: UpdateUserPayload = {
          email: formData.email.trim(),
          name: fullName,
          role: formData.role,
          accountStatus: formData.accountStatus,
          isActive: formData.isActive,
          partnerCompanyId: partnerCompany?.id || null,
        };
        await onSubmit(payload);
      } else {
        // Create mode now supports optional partner-company assignment too.
        const parsedTrialDays = Number.parseInt(trialDays, 10);
        const payload: CreateUserPayload = {
          email: formData.email.trim(),
          name: fullName,
          role: formData.role,
          ...(partnerCompany?.id ? { partnerCompanyId: partnerCompany.id } : {}),
          // Feature 100 — only meaningful for new FP accounts; validateForm has
          // already rejected an unusable length by this point.
          ...(asTrial && formData.role === 'FINANCIAL_ADVISOR'
            ? { trialDays: parsedTrialDays }
            : {}),
          // Feature 100 — funnel linkage + firm name back from the /partner-trial request
          ...(trialPrefill && formData.role === 'FINANCIAL_ADVISOR'
            ? {
                trialRequestId: trialPrefill.trialRequestId,
                ...(trialPrefill.companyName ? { companyName: trialPrefill.companyName } : {}),
              }
            : {}),
        };
        await onSubmit(payload);
      }
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | UserRole | AccountStatus,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-form-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="user-form-title" className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit User' : 'Create New User'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value as UserRole)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Feature 100 — create-as-trial (new FP accounts only) */}
          {!isEditMode && formData.role === 'FINANCIAL_ADVISOR' && (
            <div className="rounded-md border border-[#e7dcc4] bg-[#faf6ee] p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={asTrial}
                  onChange={(e) => setAsTrial(e.target.checked)}
                  disabled={isSubmitting}
                />
                Create as Trial FP account
              </label>
              {asTrial && (
                <div className="mt-2 flex items-center gap-2">
                  <label htmlFor="trialDays" className="text-sm text-gray-700">
                    Trial length (days)
                  </label>
                  <input
                    id="trialDays"
                    type="number"
                    min={1}
                    max={365}
                    value={trialDays}
                    onChange={(e) => {
                      setTrialDays(e.target.value);
                      setTrialDaysError(undefined);
                    }}
                    disabled={isSubmitting}
                    aria-invalid={!!trialDaysError}
                    className="w-24 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857]"
                  />
                </div>
              )}
              {trialDaysError && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {trialDaysError}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Full FP access with a countdown; trial welcome email replaces the regular
                onboarding drip. Extend or convert later from the advisor editor.
              </p>
            </div>
          )}

          {/* Account Status */}
          <div>
            <label htmlFor="accountStatus" className="block text-sm font-medium text-gray-700 mb-1">
              Account Status <span className="text-red-500">*</span>
            </label>
            <select
              id="accountStatus"
              value={formData.accountStatus}
              onChange={(e) => handleChange('accountStatus', e.target.value as AccountStatus)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Is Active */}
          <div className="flex items-center gap-2">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              disabled={isSubmitting}
              className="h-4 w-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857] disabled:cursor-not-allowed"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active user
            </label>
          </div>

          <PartnerCompanySelect
            label="Partner Company"
            value={partnerCompany}
            onChange={setPartnerCompany}
            disabled={isSubmitting}
            placeholder="Search partner companies..."
          />
          {isProfessionalRole && partnerCompany && (
            <p className="text-xs text-amber-700">
              If the selected partner company has SSO enabled, saving will clear the user&apos;s
              current Kinde identity so their next enterprise sign-in can relink the account.
            </p>
          )}

          {/* Form actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <span>{isEditMode ? 'Update User' : 'Create User'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
